class ProductVariantMessage extends HTMLElement {
  connectedCallback() {
    this.message = this.querySelector('[data-product-variant-message]');
    this.announcer = this.querySelector('[data-product-variant-announcer]');
    this.config = this.#readConfig();
    this.abortController = new AbortController();

    document.addEventListener('variant:update', this.#onVariantUpdate, {
      signal: this.abortController.signal,
    });

    this.update(this.config.selectedVariantId);
    this.isInitialised = true;
  }

  /**
   * Availability is conveyed visually by toggling `hidden` on the message, which
   * is not a reliable live-region trigger. Mirror the effective message into a
   * separate, always-present status region instead. Skipped on first render so
   * page load is not announced.
   */
  announce(text) {
    if (!this.isInitialised || !this.announcer) return;

    const next = (text || '').trim();
    if (this.announcer.textContent === next) return;

    this.announcer.textContent = next;
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  #readConfig() {
    const script = this.querySelector('script[type="application/json"]');

    if (!script?.textContent) return {};

    try {
      return JSON.parse(script.textContent);
    } catch {
      return {};
    }
  }

  #onVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;

    this.update(event.detail?.resource?.id);
  };
}

class ProductInStockMessage extends ProductVariantMessage {
  update(variantId) {
    if (this.config.hasFarewellTag) {
      this.message.hidden = true;
      this.announce('');
      return;
    }

    const variant = this.config.variants?.[String(variantId)];
    const minimumQuantity = Number(this.config.minimumQuantity || 1);

    this.message.hidden = !variant || Number(variant.inventoryQuantity) < minimumQuantity;
    this.announce(this.message.hidden ? '' : this.message.textContent);
  }
}

class ProductMadeToOrderMessage extends ProductVariantMessage {
  update(variantId) {
    const variant = this.config.variants?.[String(variantId)];
    if (!variant) {
      this.message.hidden = true;
      this.announce('');
      return;
    }

    const message = this.#messageForVariant(variant);
    this.message.textContent = message;
    this.message.hidden = !message;
    this.announce(message);
  }

  #messageForVariant(variant) {
    const { mode, tags, messages, isPairProduct, quantityDefault } = this.config;
    const inventoryQuantity = Number(variant.inventoryQuantity);

    // Farewell is a merchandising decision made purely by tag, independent of the
    // pair/inventory/availability metafields the other MTO messages key off of.
    // It's checked once here, before either mode branches, and only the 'oos'
    // instance is allowed to render it (the 'pairs' branch never checks it) so a
    // product with both block instances on the page never shows it twice.
    if (mode === 'oos' && tags.farewell) return messages.farewell;

    if (mode === 'pairs') {
      if (!isPairProduct || inventoryQuantity > 1) return '';

      if (tags.mto23) return messages.mto23;
      if (tags.mto68 && Number(quantityDefault) < 1) return messages.mto68;
      if (tags.mto46 && inventoryQuantity < 1) return messages.mto46;

      return '';
    }

    if (mode === 'oos') {
      if (isPairProduct || String(variant.availabilityType || '').trim() !== '') return '';

      if (tags.mto23) return messages.mto23;
      if (tags.mtoFj24) return messages.mtoFj24;
      if (tags.mto46 && inventoryQuantity < 1) return messages.mto46;
      if (tags.mto68) return messages.mto68;
    }

    return '';
  }
}

if (!customElements.get('product-in-stock-message')) {
  customElements.define('product-in-stock-message', ProductInStockMessage);
}

if (!customElements.get('product-made-to-order-message')) {
  customElements.define('product-made-to-order-message', ProductMadeToOrderMessage);
}
